import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getAllSurveys, getSurveyInfo } from '@redux/survey/thunks';
import { getUserInfo } from '@redux/auth/thunks';
import { tokenSelector, userSelector, actionResultSelector as authActionResultSelector } from '@redux/auth';
import { actionResultSelector, currentSurveyInfoSelector, surveysSelector } from '@redux/survey';
import {
  useBodyScrollLock,
  useMount,
  useRouter,
  useUnMount,
} from '@hooks';
import ConditionalRenderer, { Else, If } from '@components/ConditionalRenderer';
import { Checkbox, message } from 'antd';
import { PATHS } from '@constants';
import { handleLoading, handleOperationSuccess } from '@utils';
import Header from '@layouts/Header';
import Footer from '@layouts/Footer';
import InnerHeading from '@components/InnerHeading';
import StudyItem from '@components/StudyItem';
import Button from '@components/Button';
import Modal from '@components/Modal';
import ModalHeading from '@components/ModalHeading';
import UpdateInfoForm from '@components/UpdateInfoForm';
import Loader from '@components/Loader';
import Empty from '@components/Empty';
import decorRight from '@img/decor/studies-right.png';
import headingIcon from '@img/list.png';
import lockIcon from '@img/locked.png';
import smileIcon from '@img/smile-lg.png';
import studyIcon from '@img/study.png';
import './style.scss';

const Studies = () => {
  const dispatch = useDispatch();

  const [isAccessModalVisible, setIsAccessModalVisible] = useState(false);
  const [isSpecialSurveyModalVisible, setIsSpecialSurveyModalVisible] = useState(false);
  const [isUpdateInfoModalVisible, setIsUpdateInfoModalVisible] = useState(false);
  const [isSurveyModalVisible, setIsSurveyModalVisible] = useState(false);
  const [startSurveyButtonDisabled, setStartSurveyButtonDisabled] = useState(false);
  const { lockScroll, unlockScroll } = useBodyScrollLock();

  const user = useSelector(userSelector);
  const isAffected = user?.profile?.haveBeenAffectedByCancer;
  const token = useSelector(tokenSelector);
  const actionResult = useSelector(actionResultSelector);
  const authActionResult = useSelector(authActionResultSelector);
  const currentSurveyInfo = useSelector(currentSurveyInfoSelector);
  const surveys = useSelector(surveysSelector);

  const { push } = useRouter();

  const updateInfoBack = () => {
    setIsUpdateInfoModalVisible(false);
    setIsSpecialSurveyModalVisible(true);
  };

  const handleCheckboxChange = (e) => {
    setStartSurveyButtonDisabled(e.target.checked);
  };

  useMount(() => {
    dispatch(getAllSurveys());
  });

  useUnMount(() => {
    unlockScroll();
  });

  useEffect(() => {
    if (handleOperationSuccess(authActionResult, 'updateProfile')) {
      setIsUpdateInfoModalVisible(false);

      dispatch(getUserInfo());
      message.success('Your Profile was successfully updated');
    }
  }, [authActionResult]);

  return (
    <>
      <Header />
      <InnerHeading
        className='studies'
        decorRight={decorRight}
        title='Studies'
        icon={headingIcon}
        iconFirst
      />
      <div className='studies-list'>
        <div className='row'>
          <ConditionalRenderer condition={handleLoading(actionResult, 'getAllSurveys')}>
            <If>
              <div className='column sm-12' >
                <Loader />
              </div>
            </If>
            <Else>
              {
                surveys.length
                  ? surveys?.map(({
                    id, image, name, accessibleFor, passed, active,
                  }) => (
                    !!active && <div className='column sm-6 md-4 lg-3 flex' key={id}>
                      <StudyItem
                        image={image}
                        title={name}
                        authStudy={accessibleFor !== 'All_USERS'}
                        passed={passed}
                        specialStudy={accessibleFor === 'AFFECTED_USERS'}
                        isAffected={isAffected}
                        showAccessModal={() => {
                          setIsAccessModalVisible(true);
                          lockScroll();
                        }}
                        showSpecialSurveyModal={() => {
                          setIsSpecialSurveyModalVisible(true);
                          lockScroll();
                        }}
                        showStudyModal={() => {
                          dispatch(getSurveyInfo(id));
                          setIsSurveyModalVisible(true);
                          lockScroll();
                        }}
                      />
                    </div>
                  ))
                  : <Empty text='No Surveys Yet' />
              }
            </Else>
          </ConditionalRenderer>
        </div>
      </div>
      <Modal
        isVisible={isAccessModalVisible}
        closeModal={() => {
          setIsAccessModalVisible(false);
          unlockScroll();
        }}
        classNames='access-modal center-modal popover-container popover-container_mobile'
        popover
        size='sm'
        icon={lockIcon}
        title='Please log in to access this section'
        button={
          <Button
            className='field-text tablet-field-text mobile-field-text'
            theme='primary'
            title='Login'
            linkTo={PATHS.SIGN_IN}
          />
        }
      />
      <Modal
        isVisible={isSurveyModalVisible}
        closeModal={() => {
          setIsSurveyModalVisible(false);
          unlockScroll();
        }}
        classNames='survey-modal center-modal popover-container popover-container_mobile'
        popover
        size='lg'
        icon={studyIcon}
        titleSm={currentSurveyInfo.passed ? 'You have successfully passed this survey' : 'About study'}
        desc={currentSurveyInfo.description}
        action={!currentSurveyInfo.passed
          && <Checkbox className='custom-checkbox' onChange={handleCheckboxChange}>
            I consent to Vann processing my health and other special category data
          </Checkbox>
        }
        button={
          !currentSurveyInfo.passed
          && <Button
            className='field-text tablet-field-text mobile-field-text'
            theme='main'
            title='Start survey'
            onClick={() => push(PATHS.SURVEY(currentSurveyInfo.surveyId))}
            disabled={!startSurveyButtonDisabled}
          />
        }
      />
      <Modal
        isVisible={isSpecialSurveyModalVisible}
        closeModal={() => {
          setIsSpecialSurveyModalVisible(false);
          unlockScroll();
        }}
        classNames='special-survey-modal center-modal popover-container popover-container_mobile'
        popover
        size='md'
        icon={smileIcon}
        title='This survey is only for those affected by cancer'
        desc={'Switch your account type to I have been affected by cancer” to gain access. You\'ll need to fill in some additional fields'}
        button={
          <Button
            className='field-text tablet-field-text mobile-field-text'
            theme='primary'
            title='I have been affected by cancer'
            onClick={() => {
              setIsSpecialSurveyModalVisible(false);
              setIsUpdateInfoModalVisible(true);
            }}
          />
        }
      />
      <ConditionalRenderer condition={token}>
        <If>
          <Modal
            isVisible={isUpdateInfoModalVisible}
            closeModal={() => {
              setIsUpdateInfoModalVisible(false);
              unlockScroll();
            }}
            classNames='update-info-modal no-padding center-modal popover-container popover-container_mobile'
            popover
            size='xl'
            heading={<ModalHeading title='Update my information' icon={smileIcon} onBack={updateInfoBack} />}
            body={<UpdateInfoForm />}
          />
        </If>
      </ConditionalRenderer>
      <Footer />
    </>
  );
};

export default Studies;
